import { format } from 'date-fns';
import {
  ResponseActivityV3ByStudentDto,
  ResponseActivityV3SessionByStudentDto,
  StudentActivityV3,
} from 'src/generated/model';

export const checkSubmitted = (studentActivityV3: StudentActivityV3 | undefined, filter?: string): boolean => {
  if (!studentActivityV3) return false;
  switch (filter) {
    case 'studentText':
      return !!studentActivityV3?.studentText;
    case 'record':
      return !!studentActivityV3?.records?.length;
    case 'summary':
      return !!studentActivityV3?.summary;
    default:
      return !!studentActivityV3?.studentText && !!studentActivityV3?.record && !!studentActivityV3?.summary;
  }
};

export const getFilteredSessions = (sessions: ResponseActivityV3SessionByStudentDto[] | undefined) => {
  if (!sessions) return [];

  const filteredSessions = sessions.filter((session) => {
    if (session.type === 'NOTICE') return false;
    return true;
  });

  if (!filteredSessions.some((session) => session.activitySessionOrder)) {
    return filteredSessions;
  }

  return [...filteredSessions].sort((a, b) => {
    const orderA = a.activitySessionOrder?.order ?? 0;
    const orderB = b.activitySessionOrder?.order ?? 0;
    return orderA - orderB;
  });
};
export const getFilteredSessionsForTodo = (sessions: ResponseActivityV3SessionByStudentDto[] | undefined) => {
  if (!sessions) return [];

  // NOTICE 타입의 세션과 종료된 제출완료 세션을 필터링
  const filteredSessions = sessions.filter((session) => {
    // NOTICE 타입 필터링
    if (session.type === 'NOTICE') return false;
    if (session.studentActivitySessions && session.studentActivitySessions.length > 0) return false;

    // 기간이 지나고 제출이 완료된 세션 필터링
    // endDate가 없는 경우 마감되지 않은 것으로 처리 (isEnded = false)
    const isSessionEnded = isEnded(session.endDate, session.lateSubmissionEndAt, session.isLateSubmissionAllowed);

    // 세션이 종료된 경우
    if (isSessionEnded) {
      // 제출한 세션인 경우 필터링 (표시하지 않음)
      const isSubmitted = !!session.studentActivitySessions && session.studentActivitySessions.length > 0;
      return !isSubmitted;
    }

    // 세션이 종료되지 않은 경우는 모두 표시
    return true;
  });

  if (!filteredSessions.some((session) => session.activitySessionOrder)) {
    return filteredSessions;
  }

  return filteredSessions;
};

export const isEnded = (
  endDate: string | undefined,
  lateSubmissionEndAt?: string | undefined,
  isLateSubmissionAllowed?: boolean,
) => {
  // 지각제출이 허용된 경우, lateSubmissionEndAt을 기준으로 판단
  if (isLateSubmissionAllowed && lateSubmissionEndAt) {
    return new Date(lateSubmissionEndAt) < new Date();
  }
  // 지각제출이 허용되지 않거나, endDate만 있는 경우 endDate 기준으로 판단
  return endDate ? new Date(endDate) < new Date() : false;
};

const isValidDate = (dateStr: string | undefined): boolean => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  return !isNaN(date.getTime());
};

// 현재 시간이 제출 가능 시간대 내에 있는지 확인하는 함수
const isWithinSubmissionTimeWindow = (session: ResponseActivityV3SessionByStudentDto): boolean => {
  if (!session.hasSubmissionTimeWindow) return false;

  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();

  // 시작 시간과 종료 시간을 분으로 변환하여 비교
  const startTimeInMinutes = session.submitStartHour * 60 + session.submitStartMinute;
  const endTimeInMinutes = session.submitEndHour * 60 + session.submitEndMinute;
  const currentTimeInMinutes = currentHour * 60 + currentMinute;

  return currentTimeInMinutes >= startTimeInMinutes && currentTimeInMinutes <= endTimeInMinutes;
};

// 시간 포맷팅 함수 (HH:MM 형식)
const formatTimeHHMM = (hour: number, minute: number): string => {
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
};

export const getSessionTimeInfo = (session: ResponseActivityV3SessionByStudentDto) => {
  const now = new Date();
  const startDate = isValidDate(session.startDate) ? new Date(session.startDate) : null;
  const endDate = isValidDate(session.endDate) ? new Date(session.endDate) : null;
  const lateSubmissionStartAt = isValidDate(session.lateSubmissionStartAt)
    ? new Date(session.lateSubmissionStartAt)
    : null;
  const lateSubmissionEndAt = isValidDate(session.lateSubmissionEndAt) ? new Date(session.lateSubmissionEndAt) : null;

  // 모든 시간 데이터가 없는지 확인
  const hasNoTimeData = !startDate && !endDate && !lateSubmissionStartAt && !lateSubmissionEndAt;

  // 제출 가능 시간대 관련 정보
  const hasTimeWindow = session.hasSubmissionTimeWindow;
  const isInTimeWindow = isWithinSubmissionTimeWindow(session);
  const submissionTimeWindow = hasTimeWindow
    ? `${formatTimeHHMM(session.submitStartHour, session.submitStartMinute)} ~ ${formatTimeHHMM(session.submitEndHour, session.submitEndMinute)}`
    : '';

  // 세션 상태 결정
  type SessionState =
    | 'BEFORE_START'
    | 'IN_PROGRESS'
    | 'BETWEEN_END_AND_LATE'
    | 'LATE_SUBMISSION'
    | 'AFTER_LATE_END'
    | 'AFTER_END'
    | 'NO_TIME_LIMITS'; // 시간 제한 없는 상태 추가

  let sessionState: SessionState;

  // 시간 데이터가 없는 경우 특별 상태로 처리
  if (hasNoTimeData) {
    sessionState = 'NO_TIME_LIMITS';
  } else if (startDate && now < startDate) {
    sessionState = 'BEFORE_START';
  } else if ((startDate && endDate && now >= startDate && now < endDate) || (!startDate && endDate && now < endDate)) {
    sessionState = 'IN_PROGRESS';
  } else if (lateSubmissionStartAt && endDate && now >= endDate && now < lateSubmissionStartAt) {
    sessionState = 'BETWEEN_END_AND_LATE';
  } else if (
    lateSubmissionEndAt &&
    lateSubmissionStartAt &&
    now >= lateSubmissionStartAt &&
    now < lateSubmissionEndAt
  ) {
    sessionState = 'LATE_SUBMISSION';
  } else if (lateSubmissionEndAt && now >= lateSubmissionEndAt) {
    sessionState = 'AFTER_LATE_END';
  } else if (endDate && now >= endDate) {
    sessionState = 'AFTER_END';
  } else {
    sessionState = 'IN_PROGRESS';
  }

  // 상태별 정보 설정
  let timeText = '';
  let dateToShow = '';

  switch (sessionState) {
    case 'NO_TIME_LIMITS':
      timeText = '';
      dateToShow = '';
      break;
    case 'BEFORE_START':
      timeText = '차시 시작시각';
      dateToShow = session.startDate;
      break;
    case 'IN_PROGRESS':
      timeText = '차시 종료시각';
      dateToShow = session.endDate;
      break;
    case 'BETWEEN_END_AND_LATE':
      timeText = '지각 제출 시작시각';
      dateToShow = session.lateSubmissionStartAt;
      break;
    case 'LATE_SUBMISSION':
      timeText = '지각 제출 종료시각';
      dateToShow = session.lateSubmissionEndAt;
      break;
    case 'AFTER_LATE_END':
      timeText = '제출 기간 종료';
      dateToShow = session.lateSubmissionEndAt;
      break;
    case 'AFTER_END':
      timeText = '제출 기간 종료';
      dateToShow = session.endDate;
      break;
  }

  // 날짜 포맷팅
  const formattedDate = dateToShow ? format(new Date(dateToShow), 'yyyy.MM.dd HH:mm') : '';

  // 수정 불가 사유 타입 정의
  type EditableStatus =
    | 'EDITABLE' // 수정 가능
    | 'NOT_STARTED' // 아직 시작되지 않음
    | 'ENDED' // 제출 기간 종료
    | 'BETWEEN_SESSIONS' // 세션 사이 기간
    | 'OUTSIDE_TIME_WINDOW'; // 제출 가능 시간대 외

  // 수정 가능 여부 및 사유 결정
  let editableStatus: EditableStatus;

  if (sessionState === 'NO_TIME_LIMITS') {
    // 시간 데이터가 없는 경우 항상 수정 가능
    editableStatus = 'EDITABLE';
  } else if (sessionState === 'BEFORE_START') {
    editableStatus = 'NOT_STARTED';
  } else if (sessionState === 'AFTER_END' || sessionState === 'AFTER_LATE_END') {
    editableStatus = 'ENDED';
  } else if (sessionState === 'BETWEEN_END_AND_LATE') {
    editableStatus = 'BETWEEN_SESSIONS';
  } else if (hasTimeWindow && !isInTimeWindow) {
    editableStatus = 'OUTSIDE_TIME_WINDOW';
  } else {
    // IN_PROGRESS 또는 LATE_SUBMISSION 상태이며 시간대 제한 내에 있거나 시간대 제한이 없는 경우
    editableStatus = 'EDITABLE';
  }

  return {
    timeText,
    formattedDate,
    isBeforeStart: sessionState === 'BEFORE_START',
    isDuringSession: sessionState === 'IN_PROGRESS' || sessionState === 'NO_TIME_LIMITS',
    isAfterEnd: sessionState === 'AFTER_END' || sessionState === 'AFTER_LATE_END',
    isLateSubmissionAvailable: session.isLateSubmissionAllowed && sessionState === 'LATE_SUBMISSION',
    hasSubmissionTimeWindow: hasTimeWindow,
    isInSubmissionTimeWindow: isInTimeWindow,
    submissionTimeWindow: submissionTimeWindow,
    // 단순화된 수정 가능 여부 정보
    isEditable: editableStatus === 'EDITABLE',
    editableStatus: editableStatus,
    editableStatusText: getEditableStatusText(editableStatus),
    // 시간 데이터 없음 플래그 추가
    hasNoTimeData: hasNoTimeData,
  };
};

// 수정 불가 사유 텍스트 반환 함수
const getEditableStatusText = (
  status: 'EDITABLE' | 'NOT_STARTED' | 'ENDED' | 'BETWEEN_SESSIONS' | 'OUTSIDE_TIME_WINDOW',
): string => {
  switch (status) {
    case 'EDITABLE':
      return '제출하기';
    case 'NOT_STARTED':
      return '해당 차시는 시작되지 않았습니다';
    case 'ENDED':
      return '해당 차시의 활동은 종료되었습니다';
    case 'BETWEEN_SESSIONS':
      return '지각 제출 기간이 아닙니다';
    case 'OUTSIDE_TIME_WINDOW':
      return '제출 가능 시간대가 아닙니다';
  }
};

const sortActivityV3 = (activityv3s: ResponseActivityV3ByStudentDto[] | undefined) => {
  if (!activityv3s) return [];

  return [...activityv3s].sort((a, b) => {
    // 1. 활동 종료 여부 확인 (endDate 기준)
    const now = new Date();
    const isActivityEndedA = a.endDate ? new Date(a.endDate) < now : false;
    const isActivityEndedB = b.endDate ? new Date(b.endDate) < now : false;

    // 종료된 활동은 후순위로
    if (isActivityEndedA && !isActivityEndedB) return 1;
    if (!isActivityEndedA && isActivityEndedB) return -1;

    // 2. 새로운 차시가 있는 활동을 우선 정렬
    const hasNewSessionA =
      a.activitySessions?.some((session) => {
        // 세션이 종료되지 않았고, 학생 제출이 없는 세션을 새 차시로 간주
        const isSessionEnded = isEnded(session.endDate, session.lateSubmissionEndAt, session.isLateSubmissionAllowed);
        const isSubmitted = !!session.studentActivitySessions && session.studentActivitySessions.length > 0;
        return !isSessionEnded && !isSubmitted && session.type !== 'NOTICE';
      }) ?? false;

    const hasNewSessionB =
      b.activitySessions?.some((session) => {
        const isSessionEnded = isEnded(session.endDate, session.lateSubmissionEndAt, session.isLateSubmissionAllowed);
        const isSubmitted = !!session.studentActivitySessions && session.studentActivitySessions.length > 0;
        return !isSessionEnded && !isSubmitted && session.type !== 'NOTICE';
      }) ?? false;

    // 새 차시가 있는 활동을 우선
    if (hasNewSessionA && !hasNewSessionB) return -1;
    if (!hasNewSessionA && hasNewSessionB) return 1;

    // 3. 둘 다 새 차시가 있거나 없는 경우, 생성일 기준 최신순 정렬
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
};

// 함수 내보내기 추가
export { sortActivityV3 };

export const getUnsubmittedSessionsAsSeparateItems = (
  activities: ResponseActivityV3ByStudentDto[],
  sortType: 'DESC' | 'ASC',
) => {
  const result: Array<ResponseActivityV3ByStudentDto> = [];

  activities.forEach((activity) => {
    const filteredSessions = getFilteredSessionsForTodo(activity.activitySessions);

    if (filteredSessions.length > 0) {
      if (sortType === 'DESC') {
        // 최신 생성 순 (createdAt 기준)
        filteredSessions.sort((a, b) => {
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          return dateB - dateA; // 내림차순
        });
      } else {
        // 마감기한 순 (endDate 기준)
        filteredSessions.sort((a, b) => {
          const dateA = new Date(a.endDate).getTime();
          const dateB = new Date(b.endDate).getTime();
          return dateA - dateB; // 오름차순
        });
      }

      // 정렬된 세션을 각각 개별 항목으로 추가
      filteredSessions.forEach((session) => {
        result.push({
          ...activity,
          activitySessions: [session],
        });
      });
    }
  });

  return result;
};
